/**
 * Listing public facts (founder decision, Joe, 2026-10-22): the small set of
 * facts staff approve per basic listing — an About summary in Compaire's
 * words with its source, service types from a closed list, markets served and
 * the year founded. Migration 20261023120000_listing_public_facts.sql.
 *
 * The database is the authority: public.staff_set_org_listing_facts validates
 * through private.normalize_listing_facts and raises the same identifiers.
 * This module mirrors that rule so the Console can say what is wrong before a
 * round trip, and it is shared by the Edge Function (console-org-enrichment,
 * action set_listing_facts) and the browser (src/lib/listingFacts.ts).
 *
 * Plain TypeScript with the RPC injected: no Deno globals, so Vitest runs it.
 */
import { looksLikePricing } from "./noPricing.ts";

/**
 * The closed service-type list: the agent preference vocabulary
 * (src/lib/fit/preferenceOptions.ts SERVICE_TYPE_OPTIONS), so a listing's
 * service types and an agent's preferences compare like with like. The
 * migration's CHECK constraint and normalizer carry the same six values;
 * listingFacts.test.ts pins all three.
 */
export const LISTING_SERVICE_TYPES = [
  "Residential",
  "Luxury",
  "Commercial",
  "New Construction",
  "Property Management",
  "Land & Development",
] as const;

export type ListingServiceType = (typeof LISTING_SERVICE_TYPES)[number];

export const LISTING_SUMMARY_MIN = 40;
export const LISTING_SUMMARY_MAX = 400;
export const LISTING_MARKETS_MAX = 12;
export const LISTING_MARKET_MAX_CHARS = 60;
export const LISTING_SUMMARY_MAX_SENTENCES = 3;
const MAX_REASON_LENGTH = 500;

export interface ListingFactsInput {
  summary: string | null;
  source_url: string | null;
  service_types: ListingServiceType[];
  markets: string[];
  founded_year: number | null;
}

export type ListingFactsCode =
  | "invalid_facts"
  | "invalid_summary"
  | "invalid_characters"
  | "summary_sentences"
  | "summary_first_person"
  | "facts_pricing"
  | "facts_forbidden_topic"
  | "summary_needs_source"
  | "invalid_source_url"
  | "invalid_service_types"
  | "invalid_markets"
  | "invalid_founded_year"
  | "empty_facts"
  | "summary_copied";

export const LISTING_FACTS_ERROR_COPY: Record<ListingFactsCode, string> = {
  invalid_facts: "Only the summary, source, service types, markets and founded year can be approved.",
  invalid_summary: `The summary must be ${LISTING_SUMMARY_MIN}–${LISTING_SUMMARY_MAX} characters.`,
  invalid_characters:
    "Use Latin letters only, and remove invisible or control characters (zero-width spaces, soft hyphens, direction marks).",
  summary_sentences: `Keep the summary to ${LISTING_SUMMARY_MAX_SENTENCES} sentences or fewer.`,
  summary_first_person:
    "Write the summary in the third person (no “we”, “our”, “us”, “me”, “my”, “I”, “you” or “your”).",
  facts_pricing: "Take out prices, fees, splits or percentages. Compensation never appears on a listing.",
  facts_forbidden_topic:
    "Leave out leads, fees, benefits, perks, training and support, tools and technology, culture, rankings and performance figures (like agent or transaction counts). Those are the brokerage's to share.",
  summary_needs_source: "A summary needs the http(s) page it was summarized from.",
  invalid_source_url: "The source must be a plain http(s) address (ASCII only, no spaces, backslashes or credentials).",
  invalid_service_types: "Pick service types from the list.",
  invalid_markets: `Up to ${LISTING_MARKETS_MAX} place names, letters only (no numbers).`,
  invalid_founded_year: "The founded year must be between 1800 and this year.",
  empty_facts: "Nothing to approve. To remove the facts, clear them instead.",
  summary_copied: "The summary repeats the site's text (six or more words in a row). Rewrite it in Compaire’s words.",
};

// ---------------------------------------------------------------------------
// The text and source rules: mirrors of private.listing_clean_text,
// private.listing_summary_problem, private.listing_market_problem and
// private.listing_source_url_ok (migration 20261023120000). Same order of
// checks, same identifiers; listingFacts.test.ts runs the probe's cases.
// ---------------------------------------------------------------------------

/** NFKC first, then runs of ordinary whitespace become one space, trimmed. */
export function cleanListingText(text: string): string {
  return text.normalize("NFKC").replace(/[ \t\n\r]+/g, " ").replace(/^ +| +$/g, "");
}

// Soft hyphen, zero-width and bidi controls, word joiners, BOM, and every C0/C1 control.
// eslint-disable-next-line no-control-regex -- matching control characters is the point: they are refused.
const INVISIBLE = /[\u0001-\u001F\u007F-\u009F\u00AD\u200B-\u200F\u202A-\u202E\u2060-\u2064\uFEFF]/;

// SQL's \m and \M: a word is letters, digits and underscore in any script.
const W = "[\\p{L}\\p{N}_]";
const word = (body: string, flags = "iu") => new RegExp(`(?<!${W})(?:${body})(?!${W})`, flags);

const NEVER_COMMON =
  "leads?|fees?|desks?|splits?|caps?|commissions?|royalty|royalties|benefits?|insurance|401k|mentor\\p{L}*|culture|" +
  "bonus|bonuses|revenue[ -]share|profit[ -]sharing|equity|stock|free|tools?|technology|software|crm|platform|perks?|stipends?|support|coach(?:es|ed|ing)?|train(?:s|ed|er|ers|ing)?|health ?plan|retirement|top[ -]producing|ranked|number one";
const SUMMARY_NEVER = word(NEVER_COMMON);
const MARKET_NEVER = word(`${NEVER_COMMON}|agents?|producer\\p{L}*|production|volume|transactions?`);
const NUMBER_ONE = /#1(?![0-9])/;
// Latin script only (Basic Latin through Latin Extended-B, Latin Extended
// Additional), plus curly quotes, dashes and the ellipsis.
// eslint-disable-next-line no-control-regex -- the range starts at U+0001 on purpose (controls are refused earlier).
const NON_LATIN = /[^\u0001-\u024F\u1E00-\u1EFF\u2010-\u2015\u2018-\u201F\u2026]/u;
const FIRST_PERSON_ANY_CASE = word("we|our|ours|ourselves|my|mine|myself|you|your|yours|yourself|yourselves");
const FIRST_PERSON_LOWER = word("us|Us|me", "u");
const FIRST_PERSON_I = /(?:^|[^\p{L}\p{N}'’-])I(?:['’](?:m|ve|d))?(?:$|[^\p{L}\p{N}'’-])/u;
const ABBREVIATION = new RegExp(`(?<!${W})(Inc|St|Co|Corp|Ltd|LLC|Mt|Ft|Jr|Sr|Dr|Ave|Blvd|Rd|No)\\.`, "gu");
const ABBREVIATION_DOTTED = new RegExp(`(?<!${W})(?:U\\.S\\.|e\\.g\\.|i\\.e\\.)`, "gu");
const SENTENCE_END = /[.!?]+(?=\s|$|[A-Z])/g;

const PERF_WORDS = new Set([
  "agent", "agents", "transaction", "transactions", "sale", "sales", "volume",
  "production", "unit", "units", "closing", "closings", "listing", "listings",
  "homes", "sold", "realtors", "professionals", "deals",
]);
const YEAR_WORDS = new Set(["in", "since", "founded", "established", "est"]);
const COUNT_WORDS = new Set([
  "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve",
  "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen",
  "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety",
  "dozen", "dozens", "hundred", "hundreds", "thousand", "thousands", "million", "millions", "billion", "billions",
]);

/**
 * A number (digits or a spelled-out count) within three words of a
 * performance word. 1800–2099 is a year, not a count, only right after
 * in / since / founded / established / est.
 */
export function textHasPerformanceFigure(text: string): boolean {
  const tokens = text
    .toLowerCase()
    .split(/[^a-z0-9.,]+/)
    .map((t) => t.replace(/^[.,]+|[.,]+$/g, ""))
    .filter(Boolean);
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    const isYear = /^(18|19|20)[0-9]{2}$/.test(t) && i > 0 && YEAR_WORDS.has(tokens[i - 1]);
    const isCount = (/^[0-9]+([.,][0-9]+)*[km]?$/.test(t) && !isYear) || COUNT_WORDS.has(t);
    if (!isCount) continue;
    for (let j = Math.max(0, i - 3); j <= Math.min(tokens.length - 1, i + 3); j++) {
      if (PERF_WORDS.has(tokens[j])) return true;
    }
  }
  return false;
}

/** "We"/"our"/"you"... and case-sensitive "us", "Us", "me", "I", "I'm"... ("US", "Portland, ME", "I-80" pass). */
export function summaryIsFirstPerson(text: string): boolean {
  return FIRST_PERSON_ANY_CASE.test(text) || FIRST_PERSON_LOWER.test(text) || FIRST_PERSON_I.test(text);
}

export function summarySentenceCount(text: string): number {
  const stripped = text.replace(ABBREVIATION, "$1").replace(ABBREVIATION_DOTTED, "x");
  return (stripped.match(SENTENCE_END) ?? []).length;
}

// private.text_looks_like_compensation's rule (20260918200000), as
// src/lib/compensationGuard.ts states it. listingFacts.test.ts checks this
// agrees with compensationGuardError on a fixture list.
const PAIR = /(?<![\d$.])(\d{1,3})\s*(?:\/|-|–|—)\s*(\d{1,3})(?![\d.%])/g;
const PERCENT = /\d\s*%|\bpercent(?:age)?\b/i;
const SPLIT_WORD = /\bsplits?\b/i;
const COMP_WORD = "(?:cap|caps|capped|fee|fees|desk|commission|royalty|per\\s+(?:deal|transaction|month))";
const MONEY_NEAR_COMP = new RegExp(
  `\\$\\s*\\d[\\d,.]*\\s*[kK]?\\b[^.]{0,30}\\b${COMP_WORD}\\b|\\b${COMP_WORD}\\b[^.]{0,30}\\$\\s*\\d`,
  "i",
);

export function textLooksLikeCompensation(text: string): boolean {
  for (const m of text.matchAll(PAIR)) {
    if (Number(m[1]) + Number(m[2]) === 100) return true;
  }
  return PERCENT.test(text) || SPLIT_WORD.test(text) || MONEY_NEAR_COMP.test(text);
}

const reportsMoney = (text: string) => looksLikePricing(text) || textLooksLikeCompensation(text);
const codePoints = (text: string) => [...text].length;

/** Mirror of private.listing_summary_problem: null when the summary may be shown. */
export function listingSummaryProblem(text: string): ListingFactsCode | "not_normalized" | null {
  if (text !== cleanListingText(text)) return "not_normalized";
  if (INVISIBLE.test(text) || NON_LATIN.test(text)) return "invalid_characters";
  if (codePoints(text) < LISTING_SUMMARY_MIN || codePoints(text) > LISTING_SUMMARY_MAX) return "invalid_summary";
  if (summarySentenceCount(text) > LISTING_SUMMARY_MAX_SENTENCES) return "summary_sentences";
  if (summaryIsFirstPerson(text)) return "summary_first_person";
  if (reportsMoney(text)) return "facts_pricing";
  if (SUMMARY_NEVER.test(text) || NUMBER_ONE.test(text) || textHasPerformanceFigure(text)) return "facts_forbidden_topic";
  return null;
}

const MARKET = /^\p{L}[\p{L} .,'&()/-]*$/u;

/** Mirror of private.listing_market_problem: null when the place name may be shown. */
export function listingMarketProblem(text: string): ListingFactsCode | "not_normalized" | null {
  if (text !== cleanListingText(text)) return "not_normalized";
  if (INVISIBLE.test(text) || NON_LATIN.test(text)) return "invalid_characters";
  if (codePoints(text) < 2 || codePoints(text) > LISTING_MARKET_MAX_CHARS || !MARKET.test(text)) return "invalid_markets";
  if (reportsMoney(text)) return "facts_pricing";
  if (MARKET_NEVER.test(text) || NUMBER_ONE.test(text)) return "facts_forbidden_topic";
  return null;
}

const SOURCE_URL = /^https?:\/\/([A-Za-z0-9-]+\.)+[A-Za-z][A-Za-z0-9-]*(:[0-9]{1,5})?([/?#][\][A-Za-z0-9._~:/?#@!$&'()*+,;=%-]*)?$/i;

/** Mirror of private.listing_source_url_ok: an ASCII-only absolute http(s) URL, no credentials. */
export function listingSourceUrlOk(url: string): boolean {
  return url.length <= 2048 && SOURCE_URL.test(url);
}

export type NormalizeResult = { ok: true; facts: ListingFactsInput } | { ok: false; code: ListingFactsCode };

/** The mirror of private.normalize_listing_facts. */
export function normalizeListingFacts(raw: unknown, currentYear = new Date().getUTCFullYear()): NormalizeResult {
  const fail = (code: ListingFactsCode): NormalizeResult => ({ ok: false, code });
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return fail("invalid_facts");
  const input = raw as Record<string, unknown>;
  const allowed = new Set(["summary", "source_url", "service_types", "markets", "founded_year"]);
  if (Object.keys(input).some((k) => !allowed.has(k))) return fail("invalid_facts");

  let summary: string | null = null;
  if (input.summary !== undefined && input.summary !== null) {
    if (typeof input.summary !== "string") return fail("invalid_summary");
    summary = cleanListingText(input.summary) || null;
  }
  if (summary !== null) {
    const problem = listingSummaryProblem(summary);
    if (problem) return fail(problem as ListingFactsCode);
  }

  let source: string | null = null;
  if (input.source_url !== undefined && input.source_url !== null) {
    if (typeof input.source_url !== "string") return fail("invalid_source_url");
    source = input.source_url.replace(/^ +| +$/g, "") || null;
  }
  if (source !== null && !listingSourceUrlOk(source)) return fail("invalid_source_url");
  if (summary !== null && source === null) return fail("summary_needs_source");
  if (summary === null) source = null;

  let serviceTypes: ListingServiceType[] = [];
  if (input.service_types !== undefined && input.service_types !== null) {
    if (!Array.isArray(input.service_types)) return fail("invalid_service_types");
    for (const t of input.service_types) {
      if (typeof t !== "string" || !(LISTING_SERVICE_TYPES as readonly string[]).includes(t)) {
        return fail("invalid_service_types");
      }
    }
    serviceTypes = LISTING_SERVICE_TYPES.filter((t) => (input.service_types as string[]).includes(t));
  }

  const markets: string[] = [];
  if (input.markets !== undefined && input.markets !== null) {
    if (!Array.isArray(input.markets) || input.markets.length > LISTING_MARKETS_MAX) return fail("invalid_markets");
    for (const m of input.markets) {
      if (typeof m !== "string") return fail("invalid_markets");
      const text = cleanListingText(m);
      const problem = listingMarketProblem(text);
      if (problem) return fail(problem as ListingFactsCode);
      if (!markets.some((x) => x.toLowerCase() === text.toLowerCase())) markets.push(text);
    }
  }

  let year: number | null = null;
  if (input.founded_year !== undefined && input.founded_year !== null) {
    if (typeof input.founded_year !== "number" || !Number.isInteger(input.founded_year)) return fail("invalid_founded_year");
    if (input.founded_year < 1800 || input.founded_year > currentYear) return fail("invalid_founded_year");
    year = input.founded_year;
  }

  if (summary === null && serviceTypes.length === 0 && markets.length === 0 && year === null) return fail("empty_facts");
  return { ok: true, facts: { summary, source_url: source, service_types: serviceTypes, markets, founded_year: year } };
}

const looseWords = (s: string) =>
  cleanListingText(s)
    .toLowerCase()
    .replace(/[^\p{L}\p{N} ]/gu, " ")
    .split(" ")
    .filter(Boolean);

/** How many consecutive words a summary may share with the site's text. */
export const COPIED_RUN_WORDS = 6;

/**
 * "Not copied from their site": a summary that shares a run of six or more
 * consecutive words with a staged suggestion's text (the site's own words) is
 * refused. Compared loosely: case, spacing and punctuation ignored.
 */
export function summaryCopiesStagedText(summary: string, stagedTexts: readonly string[]): boolean {
  const words = looseWords(summary);
  if (words.length === 0) return false;
  const staged = stagedTexts.filter((t) => typeof t === "string").map((t) => ` ${looseWords(t).join(" ")} `);
  const runs =
    words.length < COPIED_RUN_WORDS
      ? [words.join(" ")]
      : Array.from({ length: words.length - COPIED_RUN_WORDS + 1 }, (_, i) => words.slice(i, i + COPIED_RUN_WORDS).join(" "));
  return runs.some((run) => staged.some((t) => t.includes(` ${run} `)));
}

// ---------------------------------------------------------------------------
// The action.
// ---------------------------------------------------------------------------

export type ListingFactsRpc = (args: {
  p_actor_user_id: string;
  p_org_id: string;
  p_facts: ListingFactsInput | null;
  p_reason: string;
  p_ip: string | null;
  p_user_agent: string | null;
}) => Promise<{ data: unknown; error: { message?: string } | null }>;

export interface ListingFactsResult {
  status: number;
  body: {
    ok?: true;
    outcome?: "approved" | "cleared" | "unchanged";
    facts?: ListingFactsInput | null;
    approvedAt?: string | null;
    error?: string;
    code?: string;
  };
}

/** Stable identifiers raised by staff_set_org_listing_facts → HTTP status. Anything else is a 500. */
const ERROR_STATUS = new Map<string, number>([
  ["not_staff", 403],
  ["invalid_reason", 422],
  ["org_not_found", 404],
  ["org_not_listed", 409],
  ...(Object.keys(LISTING_FACTS_ERROR_COPY) as ListingFactsCode[]).map((c) => [c, 422] as [string, number]),
]);

/**
 * The set_listing_facts action of console-org-enrichment. The Edge Function
 * has already authenticated the caller, checked the active staff gate, loaded
 * the organization and refused a seed organization. `facts: null` clears.
 * `stagedTexts` are the organization's pending text suggestions, for the
 * copy check; a failed read of them is passed as null and refuses an
 * approval with a summary (it cannot be checked).
 */
export async function setListingFacts(
  rpc: ListingFactsRpc,
  input: {
    orgId: string;
    staffId: string;
    facts: unknown;
    reason: unknown;
    stagedTexts: readonly string[] | null;
    ip: string | null;
    userAgent: string | null;
    logError?: (message: string, error?: unknown) => void;
  },
): Promise<ListingFactsResult> {
  const reason = typeof input.reason === "string" ? input.reason.trim() : "";
  if (!reason || reason.length > MAX_REASON_LENGTH) {
    return {
      status: 422,
      body: { error: `A reason of ${MAX_REASON_LENGTH} characters or fewer is required.`, code: "invalid_reason" },
    };
  }

  let facts: ListingFactsInput | null = null;
  if (input.facts !== null) {
    const normalized = normalizeListingFacts(input.facts);
    if (!normalized.ok) {
      return { status: 422, body: { error: LISTING_FACTS_ERROR_COPY[normalized.code], code: normalized.code } };
    }
    facts = normalized.facts;
    if (facts.summary !== null) {
      if (input.stagedTexts === null) {
        return { status: 503, body: { error: "The staged text could not be read to check the summary. Try again.", code: "staged_read_failed" } };
      }
      if (summaryCopiesStagedText(facts.summary, input.stagedTexts)) {
        return { status: 422, body: { error: LISTING_FACTS_ERROR_COPY.summary_copied, code: "summary_copied" } };
      }
    }
  }

  const { data, error } = await rpc({
    p_actor_user_id: input.staffId,
    p_org_id: input.orgId,
    p_facts: facts,
    p_reason: reason,
    p_ip: input.ip,
    p_user_agent: input.userAgent,
  });
  if (error) {
    const message = String(error.message ?? "");
    const status = ERROR_STATUS.get(message);
    if (!status) {
      input.logError?.("console-org-enrichment: staff_set_org_listing_facts failed", error);
      return { status: 500, body: { error: "The listing facts could not be saved. Nothing was changed." } };
    }
    const copy = (LISTING_FACTS_ERROR_COPY as Record<string, string>)[message];
    return { status, body: { error: copy ?? message, code: message } };
  }

  const result = (data ?? {}) as { outcome?: string; facts?: ListingFactsInput | null; approved_at?: string | null };
  const outcome =
    result.outcome === "approved" || result.outcome === "cleared" || result.outcome === "unchanged" ? result.outcome : null;
  if (!outcome) {
    input.logError?.("console-org-enrichment: staff_set_org_listing_facts returned an unexpected shape", data);
    return { status: 500, body: { error: "The listing facts could not be confirmed. Reload to see their state." } };
  }
  return {
    status: 200,
    body: { ok: true, outcome, facts: outcome === "approved" ? (result.facts ?? null) : null, approvedAt: result.approved_at ?? null },
  };
}

/** The suggestion keys whose text is the brokerage's own words (for the copy check). */
export const STAGED_TEXT_FIELD_KEYS = ["description", "tagline", "missionStatement", "ourStory", "whatMakesUsDifferent"] as const;

/** The Build page's read of a listing's stored facts (staff only, service role). */
export interface StoredListingFacts {
  summary: string | null;
  sourceUrl: string | null;
  serviceTypes: string[];
  markets: string[];
  foundedYear: number | null;
  approvedAt: string;
  /** False when approved for an earlier listing: it does not render until re-approved. */
  current: boolean;
}

export function storedListingFacts(
  row: {
    listed_at: string;
    summary: string | null;
    source_url: string | null;
    service_types: string[] | null;
    markets: string[] | null;
    founded_year: number | null;
    approved_at: string;
  } | null,
  orgListedAt: string | null | undefined,
): StoredListingFacts | null {
  if (!row) return null;
  return {
    summary: row.summary,
    sourceUrl: row.source_url,
    serviceTypes: row.service_types ?? [],
    markets: row.markets ?? [],
    foundedYear: row.founded_year,
    approvedAt: row.approved_at,
    current: !!orgListedAt && new Date(row.listed_at).getTime() === new Date(orgListedAt).getTime(),
  };
}
